import { useEffect, useState, type FormEvent } from 'react';
import { ProgramMembershipsPanel } from './NetworkAccounts.tsx';
import { api, useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Drawer, Empty, ErrorBox, Kbd, Loading } from '../../ui/kit.tsx';
import { SearchBox } from '../../ui/SearchBox.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { RELATIONSHIPS, type Named, type ProgramList, type ProgramRow, type Relationship } from './types.ts';

const RELATIONSHIP_TONE: Record<Relationship, string> = { approved: 'ok', pending: 'review', not_joined: 'muted', declined: 'warn', unknown: 'muted' };
const label = (value: string) => value.replace(/_/g, ' ');

export function Programs() {
  const { query, setQuery } = useRouter();
  const changes = useChanges();
  const filters = {
    q: query.get('q') || undefined,
    networkRef: query.get('network') || undefined,
    country: query.get('country') || undefined,
    relationship: (query.get('relationship') as Relationship | null) || undefined,
    mapping: (query.get('mapping') as 'mapped' | 'unmapped' | null) || undefined,
    limit: 200,
  };
  const list = useOp<ProgramList>('admin.programs.list', filters, changes);
  const rows = list.data?.rows ?? [];
  const [cursor, setCursor] = useState(0);
  const editing = query.get('program');
  const open = (ref: string) => setQuery({ program: ref });
  useEffect(() => setCursor(0), [JSON.stringify(filters)]);

  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, rows.length)),
      k: () => setCursor((c) => step(c, -1, rows.length)),
      o: () => rows[cursor] && open(rows[cursor]!.ref),
      enter: () => rows[cursor] && open(rows[cursor]!.ref),
      n: () => list.data?.canManage && open('new'),
    },
    !editing,
  );
  const select = (key: string, value: string) => setQuery({ [key]: value || null }, { replace: true });
  const current = editing === 'new' ? null : rows.find((r) => r.ref === editing);

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Programs</h1>
          <p className="muted">{list.data ? `${list.data.total} program(s): one per network and retailer, mapped to a merchant.` : ' '}</p>
        </div>
        {list.data?.canManage ? (
          <button type="button" className="btn btn-primary" onClick={() => open('new')}>
            New program <Kbd>n</Kbd>
          </button>
        ) : null}
      </header>
      <div className="toolbar">
        <SearchBox value={filters.q ?? ''} onChange={(value) => select('q', value)} placeholder="Search programs, ids, merchants" />
        <label className="inline-field">
          Network
          <select value={filters.networkRef ?? ''} onChange={(e) => select('network', e.target.value)}>
            <option value="">All</option>
            {list.data?.networks.map((n) => (
              <option key={n.ref} value={n.ref}>
                {n.name}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-field">
          Country
          <select value={filters.country ?? ''} onChange={(e) => select('country', e.target.value)}>
            <option value="">All</option>
            {list.data?.countries.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="inline-field">
          Relationship
          <select value={filters.relationship ?? ''} onChange={(e) => select('relationship', e.target.value)}>
            <option value="">All</option>
            {RELATIONSHIPS.map((r) => (
              <option key={r} value={r}>
                {label(r)}
              </option>
            ))}
          </select>
        </label>
        <div className="segmented" role="group" aria-label="Mapping">
          {[
            ['', 'All'],
            ['mapped', 'Mapped'],
            ['unmapped', 'Unmapped'],
          ].map(([value, text]) => (
            <button key={value} type="button" aria-pressed={(filters.mapping ?? '') === value} onClick={() => select('mapping', value!)}>
              {text}
            </button>
          ))}
        </div>
      </div>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && list.loading ? <Loading /> : null}
      {list.data && rows.length === 0 ? <Empty>No programs match.</Empty> : null}
      {rows.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Program</th>
              <th scope="col">Network · id</th>
              <th scope="col">Merchant</th>
              <th scope="col">Relationship</th>
              <th scope="col">Deeplinks</th>
              <th scope="col">Commission</th>
              <th scope="col">Active for</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.ref} className={index === cursor ? 'cursor' : undefined} onClick={() => open(row.ref)} onMouseEnter={() => setCursor(index)}>
                <td>
                  <span className="strong">{row.name}</span>
                  {row.status !== 'active' ? <span className="badge badge-muted">{row.status}</span> : null}
                </td>
                <td>
                  {row.network.name} <span className="mono muted">{row.networkProgramId}</span>
                </td>
                <td>{row.master ? row.master.name : <span className="text-warn">not mapped</span>}</td>
                <td>
                  <span className={`badge badge-${RELATIONSHIP_TONE[row.relationshipStatus]}`}>{label(row.relationshipStatus)}</span>
                </td>
                <td>{row.deeplinkAllowed === null ? <span className="muted">unknown</span> : row.deeplinkAllowed ? 'allowed' : <span className="text-warn">not allowed</span>}</td>
                <td>{row.commissionLabel ?? <span className="muted">—</span>}</td>
                <td>
                  {row.merchants.filter((m) => m.active).map((m) => m.country).join(', ') || <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {list.data && editing && (editing === 'new' || current) ? (
        <ProgramDrawer
          key={editing}
          data={list.data}
          program={current ?? null}
          defaultNetwork={filters.networkRef}
          onClose={() => setQuery({ program: null })}
          onSaved={(ref) => setQuery({ program: ref }, { replace: true })}
        />
      ) : null}
    </section>
  );
}

function ProgramDrawer({ data, program, defaultNetwork, onClose, onSaved }: { data: ProgramList; program: ProgramRow | null; defaultNetwork?: string; onClose: () => void; onSaved: (ref: string) => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState(() => ({
    networkRef: program?.network.ref ?? defaultNetwork ?? data.networks[0]?.ref ?? '',
    networkProgramId: program?.networkProgramId ?? '',
    name: program?.name ?? '',
    homepage: program?.homepage ?? '',
    trackingUrl: program?.trackingUrl ?? '',
    primaryCountry: program?.primaryCountry ?? '',
    status: program?.status ?? 'active',
    relationshipStatus: program?.relationshipStatus ?? 'unknown',
    deeplinkAllowed: program?.deeplinkAllowed === null || program === null ? '' : String(program.deeplinkAllowed),
    commissionType: program?.commission.type ?? 'percent',
    commissionDefault: program?.commission.default?.toString() ?? '',
    commissionMin: program?.commission.min?.toString() ?? '',
    commissionMax: program?.commission.max?.toString() ?? '',
    commissionCurrency: program?.commission.currency ?? '',
    commissionNotes: program?.commission.notes ?? '',
    networkNotes: program?.networkNotes ?? '',
  }));
  const [master, setMaster] = useState<Named | null>(program?.master ?? null);
  const [masterQuery, setMasterQuery] = useState('');
  const [masterOptions, setMasterOptions] = useState<Named[]>([]);
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const readOnly = !data.canManage;

  useEffect(() => {
    if (masterQuery.trim().length < 2) return setMasterOptions([]);
    const timer = window.setTimeout(() => {
      api.op<{ rows: Named[] }>('admin.programs.findMasters', { q: masterQuery.trim() }).then((r) => setMasterOptions(r.rows)).catch(() => setMasterOptions([]));
    }, 200);
    return () => window.clearTimeout(timer);
  }, [masterQuery]);

  const num = (value: string) => (value.trim() === '' ? undefined : Number(value));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const result = await runWrite<{ ref: string; created: boolean }>(
      toast,
      'admin.programs.save',
      {
        ...(program ? { ref: program.ref } : {}),
        networkRef: draft.networkRef,
        networkProgramId: draft.networkProgramId,
        name: draft.name,
        homepage: draft.homepage || null,
        trackingUrl: draft.trackingUrl || null,
        primaryCountry: draft.primaryCountry || null,
        status: draft.status,
        relationshipStatus: draft.relationshipStatus,
        deeplinkAllowed: draft.deeplinkAllowed === '' ? null : draft.deeplinkAllowed === 'true',
        commission: {
          type: draft.commissionType,
          default: num(draft.commissionDefault),
          min: num(draft.commissionMin),
          max: num(draft.commissionMax),
          currency: draft.commissionCurrency || undefined,
          notes: draft.commissionNotes || undefined,
        },
        networkNotes: draft.networkNotes || null,
        masterRef: master?.ref ?? null,
      },
      (r) => (r.created ? `${draft.name} created` : `${draft.name} saved`),
    );
    setSaving(false);
    if (result) onSaved(result.ref);
  };

  return (
    <Drawer title={program ? program.name : 'New program'} onClose={onClose}>
      <form onSubmit={submit}>
        <fieldset disabled={readOnly || saving} className="form-grid" style={{ border: 0, padding: 0, margin: 0 }}>
          <label>
            Network
            <select value={draft.networkRef} disabled={Boolean(program)} onChange={(e) => set('networkRef', e.target.value)}>
              {data.networks.map((n) => (
                <option key={n.ref} value={n.ref}>
                  {n.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Program id on the network
            <input required className="mono" value={draft.networkProgramId} onChange={(e) => set('networkProgramId', e.target.value)} />
          </label>
          <label className="span-2">
            Name
            <input required minLength={2} value={draft.name} onChange={(e) => set('name', e.target.value)} />
          </label>
          <div className="span-2">
            <span className="strong">Merchant</span>{' '}
            {master ? (
              <span className="aff-inline">
                {master.name}
                <button type="button" className="btn btn-ghost btn-small" onClick={() => setMaster(null)}>
                  Unmap
                </button>
              </span>
            ) : (
              <span className="text-warn">not mapped</span>
            )}
            {!readOnly ? (
              <>
                <input aria-label="Find merchant" placeholder="Find merchant to map…" value={masterQuery} onChange={(e) => setMasterQuery(e.target.value)} />
                {masterOptions.length ? (
                  <div className="chips" role="listbox" aria-label="Merchants">
                    {masterOptions.map((m) => (
                      <button
                        key={m.ref}
                        type="button"
                        className="chip"
                        onClick={() => {
                          setMaster(m);
                          setMasterQuery('');
                        }}
                      >
                        {m.name}
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
          <label>
            Relationship <small className="muted">platform account; partners below</small>
            <select value={draft.relationshipStatus} onChange={(e) => set('relationshipStatus', e.target.value as Relationship)}>
              {RELATIONSHIPS.map((r) => (
                <option key={r} value={r}>
                  {label(r)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Deeplinks
            <select value={draft.deeplinkAllowed} onChange={(e) => set('deeplinkAllowed', e.target.value)}>
              <option value="">Unknown</option>
              <option value="true">Allowed</option>
              <option value="false">Not allowed</option>
            </select>
          </label>
          <label>
            Status
            <select value={draft.status} onChange={(e) => set('status', e.target.value as typeof draft.status)}>
              <option value="active">Active</option>
              <option value="deactivated">Deactivated</option>
              <option value="deleted">Deleted</option>
            </select>
          </label>
          <label>
            Country
            <select value={draft.primaryCountry} onChange={(e) => set('primaryCountry', e.target.value)}>
              <option value="">—</option>
              {data.countries.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="span-2">
            Homepage
            <input type="url" value={draft.homepage} onChange={(e) => set('homepage', e.target.value)} />
          </label>
          <label className="span-2">
            Tracking link <small className="muted">the network’s link to the homepage; missing deeplink values are read from it</small>
            <input type="url" className="mono" value={draft.trackingUrl} onChange={(e) => set('trackingUrl', e.target.value)} />
          </label>
          <label>
            Commission
            <span className="input-pair">
              <select value={draft.commissionType} onChange={(e) => set('commissionType', e.target.value as typeof draft.commissionType)}>
                <option value="percent">%</option>
                <option value="fixed">Fixed</option>
                <option value="mixed">Mixed</option>
              </select>
              <input type="number" min={0} step="0.01" aria-label="Default commission" value={draft.commissionDefault} onChange={(e) => set('commissionDefault', e.target.value)} />
            </span>
          </label>
          <label>
            Range <small className="muted">min – max</small>
            <span className="input-pair">
              <input type="number" min={0} step="0.01" aria-label="Minimum commission" value={draft.commissionMin} onChange={(e) => set('commissionMin', e.target.value)} />
              <input type="number" min={0} step="0.01" aria-label="Maximum commission" value={draft.commissionMax} onChange={(e) => set('commissionMax', e.target.value)} />
            </span>
          </label>
          {draft.commissionType !== 'percent' ? (
            <label>
              Currency
              <input maxLength={3} value={draft.commissionCurrency} onChange={(e) => set('commissionCurrency', e.target.value.toUpperCase())} />
            </label>
          ) : null}
          <label className="span-2">
            Commission notes
            <input value={draft.commissionNotes} onChange={(e) => set('commissionNotes', e.target.value)} />
          </label>
          <label className="span-2">
            Network notes
            <textarea rows={2} value={draft.networkNotes} onChange={(e) => set('networkNotes', e.target.value)} />
          </label>
        </fieldset>
        {program && (Object.keys(program.externalIds).length || Object.keys(program.master?.externalIds ?? {}).length) ? (
          <p className="muted small" data-external-ids>
            Provider ids: {formatIds(program.externalIds) || '—'}
            {program.master ? <> · merchant {program.master.name}: {formatIds(program.master.externalIds) || '—'}</> : null}
          </p>
        ) : null}
        {program?.merchants.length ? (
          <>
            <h3 className="section-title">Active program</h3>
            <table className="table">
              <tbody>
                {program.merchants.map((m) => (
                  <tr key={m.ref}>
                    <td>
                      {m.name} <span className="muted">{m.country}</span>
                    </td>
                    <td>{m.active ? <span className="badge badge-ok">active program</span> : <span className="muted">uses another program</span>}</td>
                    <td className="num">
                      <Link to={`/merchants/${m.ref}`}>{m.active ? 'Open merchant' : 'Switch merchant…'}</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted small">Switching moves the merchant’s links to this program, with a preview first, on the merchant page.</p>
          </>
        ) : null}
        <div className="form-actions">
          {!readOnly ? (
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {program ? 'Save' : 'Create program'}
            </button>
          ) : null}
          {program ? <Link to={`/deeplinks?network=${program.network.ref}&tab=test&program=${program.ref}`}>Test a deeplink</Link> : null}
        </div>
      </form>
      {program ? <ProgramMembershipsPanel programRef={program.ref} /> : null}
    </Drawer>
  );
}

/** `{"fmtc": "123"}` → "fmtc 123". */
function formatIds(ids: Record<string, string>): string {
  return Object.entries(ids)
    .map(([provider, id]) => `${provider} ${id}`)
    .join(', ');
}
